import clsx from 'clsx';
import React, { useContext, useEffect } from 'react';

import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { PoweredByAssemblyBadge } from 'src/features/badge/PoweredByAssemblyBadge';
import { BadgePlacement } from 'src/features/badge/poweredByBadgeUrl';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import PortalFullLogo from 'src/legacy/components/Layout/PortalFullLogo';
import PortalLogo from 'src/legacy/components/Layout/PortalLogo';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { getPublicImageURL } from 'src/utils/ImageUtils';

const GOOGLE_AUTH_CARD_HEIGHT = 580;
const AUTH_CARD_HEIGHT = 500;

interface AuthContainerProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  usePlainBackgroundImage?: boolean;
  classes?: {
    card?: string;
  };
  /**
   * Reported by the "Powered by Assembly" badge. Defaults to the sign-in
   * placement so a new auth screen cannot mis-attribute itself as a signup.
   */
  placement?: BadgePlacement;
}

export const ClientAuthContainer: React.FC<AuthContainerProps> = ({
  title,
  description,
  children,
  usePlainBackgroundImage = false,
  classes,
  placement = 'client_login',
}) => {
  const portalConfig = useContext(PortalConfigContext);
  const { EnableGrowthLoops } = useFeatureFlags();
  const googleAuthEnabled = !portalConfig.features.disableGoogleSignIn;
  const isMobileScreen = useMediaQuery('sm', 'max');
  const hasBannerImage = useMediaQuery('xl', 'min');

  // The image height and width depends on whether google auth is enabled or not.
  // For google auth enabled: 580px height to accommodate the additional button
  // For standard auth: 500px height is sufficient
  const coverImageSize = portalConfig.features.disableGoogleSignIn
    ? AUTH_CARD_HEIGHT
    : GOOGLE_AUTH_CARD_HEIGHT;

  /**
   * Generates the auth image URL with proper sizing based on device pixel ratio.
   * Uses a multiplier capped at 2x to prevent loading unnecessarily large images
   * while still supporting high DPI displays.
   * @returns {string} Optimized auth image URL with sizing parameters
   */
  const getAuthImageSrc = () => {
    const devicePixelRatio =
      typeof window === 'undefined' ? 1 : window.devicePixelRatio;
    const pxRatioMultiplier = Math.round(Math.min(devicePixelRatio, 2));
    return getPublicImageURL(portalConfig.assets.authImage.signInUrl || '', {
      resize: {
        width: coverImageSize * pxRatioMultiplier,
        height: coverImageSize * pxRatioMultiplier,
      },
    });
  };

  // Track image loading state to handle fade-in animation
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);
  const imageSrc = usePlainBackgroundImage
    ? portalConfig.assets.authImage.signInUrl
    : getAuthImageSrc();
  useEffect(() => {
    if (!hasBannerImage) return;

    // Preload image to ensure smooth transition
    const image = new Image();
    image.src = imageSrc;

    const handleImageLoaded = () => {
      setIsImageLoaded(true);
    };
    image.addEventListener('load', handleImageLoaded);

    return () => {
      image.removeEventListener('load', handleImageLoaded);
    };
  }, [imageSrc, hasBannerImage]);

  // Only show the full logo if it's configured in portal settings
  // and not using a dummy image placeholder
  const showFullLogo =
    Boolean(portalConfig.assets.logo.logoImageUrl) &&
    !portalConfig.assets.logo.logoImageUrl.includes('dummyimage');

  return (
    <div
      className={clsx(
        'h-full',
        'flex',
        'items-center',
        'justify-center',
        'flex-col',
        'w-full',
        'box-border',
        'm-0',
        'm-auto',
        'px-4',
        'sm:px-0',
        'sm:max-w-lg',
        'xl:max-w-6xl',
      )}
    >
      <div
        className={clsx(
          'drop-shadow-sm',
          'rounded-lg',
          EnableGrowthLoops && 'sm:rounded-xl',
          'bg-white',
          'flex',
          'flex-col',
          'relative',
          'overflow-hidden',
          'h-full',
          'w-full',
          'min-w-0',
          'sm:min-w-96',
          googleAuthEnabled ? 'xl:w-[1120px]' : 'xl:w-[1000px]',
          classes?.card,
        )}
      >
        {/* Stacked above the growth-loops badge bar so the bar shows behind its
            rounded bottom corners on desktop. */}
        <div
          className={clsx(
            'flex',
            'flex-1',
            'min-h-0',
            'w-full',
            'relative',
            'z-[1]',
            'bg-white',
            'overflow-hidden',
            EnableGrowthLoops && 'sm:rounded-b-xl',
          )}
        >
          <div
            className={clsx(
              'flex',
              'w-full',
              'flex-col',
              'h-full',
              'xl:w-1/2',
              'pt-8',
              'px-6',
              // Leaves room for the corner badge so it never covers the form.
              EnableGrowthLoops ? 'pb-14' : 'pb-8',
              'sm:pt-10',
              'sm:px-16',
              'sm:pb-10',
              'box-border',
              '[&>form]:grow-0',
              'overflow-y-auto',
            )}
          >
            <div
              className={clsx(
                'flex',
                'justify-center',
                'items-center',
                'w-full',
                'mx-auto',
                title ? 'mb-0' : 'mb-10',
              )}
            >
              {showFullLogo ? (
                <PortalFullLogo logoHeight={60} />
              ) : (
                <PortalLogo />
              )}
            </div>
            {title && (
              <div
                className={clsx(
                  'w-full',
                  'flex',
                  'items-center',
                  'flex-col',
                  'my-8',
                  'mx-0',
                  'gap-2',
                )}
              >
                <BaseTypography fontType="18Medium">{title}</BaseTypography>
                {description && (
                  <BaseTypography
                    className="text-slate-500"
                    fontType="13Regular"
                  >
                    {description}
                  </BaseTypography>
                )}
              </div>
            )}

            {children}
            {isMobileScreen && !EnableGrowthLoops && (
              <PoweredByAssemblyBadge
                isAuthScreen={false}
                placement={placement}
              />
            )}
          </div>
          <div
            className={clsx(
              'xl:flex',
              'xl:w-1/2',
              'bg-cover',
              'bg-center',
              'text-white',
              'flex-col',
              'justify-end',
              'transition-opacity',
              'border-r-8',
              isImageLoaded ? 'opacity-100' : 'opacity-0',
            )}
            style={{
              backgroundImage: `url(${imageSrc})`,
            }}
          />
        </div>
        {EnableGrowthLoops && (
          <PoweredByAssemblyBadge
            isAuthScreen={false}
            placement={placement}
            variant={isMobileScreen ? 'corner' : 'bar'}
          />
        )}
      </div>
      {!EnableGrowthLoops && !isMobileScreen ? (
        <div className="min-h-20">
          <PoweredByAssemblyBadge isAuthScreen={false} placement={placement} />
        </div>
      ) : null}
    </div>
  );
};
